import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Org, PlantGroup, PlantWithOrg } from '../../../types';
import { DasRP, Dastrack } from '../../../types/Device';

import { getProjectDasRPs } from '../../../apis/DasRPApi';
import {
  createPlantGroup,
  getGroupOfPlant,
  getPlantGroups,
  getProjectDastracks,
} from '../../../apis/DastrackApi';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  setDasRPs,
  setDastracks,
  setSelectedPlantGroup,
} from '../../../slices/pages/project-setting/plantManagementPageSlice';

import ErrorBoundaryContainer from '../../../components/ErrorBoundaryContainer';

import { groupCompareFn } from '../../../utils/Sort';
import ManagementGroupList from '../management/ManagementGroupList';

import PlantGroupPlantList from './PlantGroupPlantList';

const Container = styled('div', { label: 'WorkerManagementPage-Container' })`
  display: flex;
  height: calc(100vh - 87px);
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const EmptyOrg: Org = {
  id: '',
  ownerId: '',
  name: '',
  country: '',
  taxId: '',
  color: '',
  displayName: '',
  type: 'customer',
  imageURL: null,
  status: 'ACTIVE',
};

const PlantManagementPage = () => {
  const { projectId } = useParams();
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const orgMap = useAppSelector((store) => store.system.orgMap);
  const { role, policies } = useAppSelector((store) => store.projects);
  const navigate = useNavigate();
  const [searchParam, setSearchParam] = useSearchParams();
  const plantGroupIdSelected = searchParam.get('groupId');

  const {
    refetch: refetchDastrack,
    data: dastrackData,
    isSuccess: isDastrackSuccess,
  } = useQuery({
    queryKey: ['get-project-dastracks'],
    queryFn: async () => {
      let dastracks: Dastrack[] = [];

      const run = async (nextCursor?: string | null) => {
        const res = await getProjectDastracks({
          projectId: projectId as string,
          params: {
            nextCursor: nextCursor ?? undefined,
          },
        });

        dastracks = dastracks.concat(res.data.data);

        if (res.data.paging.nextCursor) {
          await run(res.data.paging.nextCursor);
        }
      };

      await run();

      return dastracks;
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (dastrackData && isDastrackSuccess) {
      dispatch(setDastracks(dastrackData));
    }
  }, [dastrackData, isDastrackSuccess]);

  const {
    refetch: refetchDasRP,
    data: dasRPData,
    isSuccess: isDasRPSuccess,
  } = useQuery({
    queryKey: ['get-project-dasrps'],
    queryFn: async () => {
      let dasrps: DasRP[] = [];

      const run = async (nextCursor?: string | null) => {
        const res = await getProjectDasRPs({
          projectId: projectId as string,
          params: {
            nextCursor: nextCursor ?? undefined,
          },
        });

        dasrps = dasrps.concat(res.data.data);

        if (res.data.paging.nextCursor) {
          await run(res.data.paging.nextCursor);
        }
      };

      await run();

      return dasrps;
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (dasRPData && isDasRPSuccess) {
      dispatch(setDasRPs(dasRPData));
    }
  }, [dasRPData, isDasRPSuccess]);

  const {
    data: plantGroups,
    refetch: refetchPlantGroup,
    isFetching: isLoadingPlantGroup,
  } = useQuery({
    queryKey: ['get-plant-groups', projectId, i18n.language],
    queryFn: async () => {
      const res = await getPlantGroups(projectId as string);
      return res.data.data.sort(
        groupCompareFn.bind({ locales: i18n.language }),
      );
    },
    enabled: !!projectId,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const selectedPlantGroup: PlantGroup | undefined = useMemo(() => {
    return plantGroups.find((group) => group.id === plantGroupIdSelected);
  }, [plantGroupIdSelected, plantGroups]);

  const {
    data: groupOfPlants,
    refetch: refetchPlantsOfAGroup,
    isFetching: isLoadingPlantOfAGroup,
  } = useQuery({
    queryKey: ['group-of-plants', projectId, selectedPlantGroup?.id, orgMap],
    queryFn: async () => {
      const [plantRes, mewpRes] = await Promise.all([
        getGroupOfPlant({
          projectId: projectId as string,
          groupId: selectedPlantGroup?.id ?? '',
          params: {
            plantType: 'plant',
          },
        }),
        getGroupOfPlant({
          projectId: projectId as string,
          groupId: selectedPlantGroup?.id ?? '',
          params: {
            plantType: 'roofProtection',
          },
        }),
      ]);
      return [...plantRes.data.data, ...mewpRes.data.data]
        .map<PlantWithOrg>((plant) => {
          return {
            ...plant,
            orgDetail: orgMap[plant.orgId] ?? EmptyOrg,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
    },
    enabled: !!selectedPlantGroup,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (selectedPlantGroup) {
      dispatch(setSelectedPlantGroup(selectedPlantGroup));
    }
  }, [selectedPlantGroup]);

  useEffect(() => {
    if (!plantGroupIdSelected && plantGroups.length > 0) {
      navigate(
        `/project-setting/${projectId}/plant-group-management?groupId=${plantGroups[0].id}`,
        { replace: true },
      );
    }
  }, [projectId, plantGroupIdSelected, plantGroups]);

  const isEditable = () => {
    const isAuthorizedCreatePlantGroup = policies.some(
      (policy) => policy.name === 'CreatePlantGroup',
    );
    return role === 'owner' || role === 'admin' || isAuthorizedCreatePlantGroup;
  };

  return (
    <Container>
      <ErrorBoundaryContainer
        key="plant-management-group-list"
        component={
          <ManagementGroupList
            data={plantGroups}
            selectedGroup={selectedPlantGroup}
            onSelect={(group) => setSearchParam({ groupId: group.id })}
            onSuccessCreateGroup={refetchPlantGroup}
            isLoadingGroup={isLoadingPlantGroup}
            isEditable={isEditable()}
            handleCreateGroup={createPlantGroup}
            resource="plant"
          />
        }
      />
      {selectedPlantGroup && (
        <ErrorBoundaryContainer
          key="plant-group-plant-list"
          component={
            <PlantGroupPlantList
              plantGroupSelected={selectedPlantGroup}
              data={groupOfPlants}
              refetchGroup={refetchPlantGroup}
              plantGroupProject={plantGroups}
              onSuccessUpdate={refetchPlantsOfAGroup}
              onSuccessBind={() => {
                refetchDastrack();
                refetchDasRP();
              }}
              isLoadingPlantOfAGroup={isLoadingPlantOfAGroup}
            />
          }
        />
      )}
    </Container>
  );
};

export default PlantManagementPage;
